import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { t, font, Button, Input } from "@realtyflows/ui";
import { getPacket, getFormLibrary, addPacketForms, removePacketForm, updatePacket, previewPacketForm } from "../api";
import useResponsive from "../hooks/useResponsive";

const CATEGORY_LABELS = {
  buyer: "Buyer Forms",
  seller: "Seller Forms",
  transaction: "Transaction Forms",
  lease: "Lease Forms",
  agent: "Agent Forms",
};

export default function PacketBuilder() {
  const { id: packetId } = useParams();
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;

  const [packet, setPacket] = useState(null);
  const [library, setLibrary] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedForms, setSelectedForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewName, setPreviewName] = useState("");

  useEffect(() => {
    Promise.all([getPacket(packetId), getFormLibrary()]).then(([p, lib]) => {
      setPacket(p);
      setLibrary(lib);
      setLoading(false);
    }).catch(err => {
      console.error("PacketBuilder load error:", err);
      navigate("/packets");
    });
  }, [packetId, navigate]);

  const refresh = async () => {
    const p = await getPacket(packetId);
    setPacket(p);
  };

  const handleAddForms = async () => {
    if (selectedForms.length === 0) return;
    await addPacketForms(packetId, selectedForms);
    setSelectedForms([]);
    setShowLibrary(false);
    await refresh();
  };

  const handleRemoveForm = async (formId) => {
    await removePacketForm(packetId, formId);
    await refresh();
  };

  const handlePreview = async (packetFormId, formName) => {
    try {
      const data = await previewPacketForm(packetId, packetFormId);
      setPreviewHtml(data.html);
      setPreviewName(data.form_name || formName);
    } catch { }
  };

  const toggleFormSelection = (formId) => {
    setSelectedForms(prev => prev.includes(formId) ? prev.filter(id => id !== formId) : [...prev, formId]);
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", fontFamily: font.body, color: t.textMuted }}>
      Loading...
    </div>
  );

  const forms = packet?.forms || [];
  const completedCount = forms.filter(f => f.is_complete).length;
  const addedFormIds = new Set(forms.map(f => f.form_id));

  // Filter library forms by search
  const filteredLibrary = {};
  if (library?.forms) {
    for (const [cat, catForms] of Object.entries(library.forms)) {
      const filtered = catForms.filter(f =>
        !addedFormIds.has(f.id) &&
        (search === "" || f.name.toLowerCase().includes(search.toLowerCase()))
      );
      if (filtered.length > 0) filteredLibrary[cat] = filtered;
    }
  }

  return (
    <div style={{ height: "100%", overflow: "auto", background: t.pageBg }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: isMobile ? 16 : 32 }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span onClick={() => navigate("/packets")} style={{ fontSize: 12, color: t.textMuted, cursor: "pointer", fontFamily: font.body }}>&larr; Packets</span>
          </div>
          <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 600, color: t.navyLight, fontFamily: font.display, marginBottom: 4 }}>
            {packet?.name}
          </h1>
          <div style={{ fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
            {packet?.client_name && <span>{packet.client_name}</span>}
            {packet?.property_address && <span> — {packet.property_address}</span>}
            {forms.length > 0 && <span> — {completedCount}/{forms.length} forms complete</span>}
          </div>
        </div>

        {/* Shared fields preview */}
        {Object.keys(packet?.shared_fields || {}).length > 0 && (
          <div style={{
            padding: 16, background: `${t.gold}08`, border: `1px solid ${t.gold}25`,
            borderRadius: 10, marginBottom: 24,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.gold, fontFamily: font.body, marginBottom: 8, letterSpacing: "0.5px", textTransform: "uppercase" }}>
              Auto-Filled from CRM
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
              {Object.entries(packet.shared_fields).filter(([, v]) => v).map(([key, val]) => (
                <div key={key} style={{ fontSize: 12, color: t.textSecondary, fontFamily: font.body }}>
                  <span style={{ color: t.textMuted }}>{key}:</span> <strong>{val}</strong>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Forms in packet */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>
              Forms in Packet ({forms.length})
            </span>
            <Button variant="outline" size="sm" onClick={() => setShowLibrary(true)}>+ Add Forms</Button>
          </div>

          {forms.length === 0 ? (
            <div style={{
              padding: 40, textAlign: "center", background: t.surface, border: `1.5px dashed ${t.border}`,
              borderRadius: 10,
            }}>
              <div style={{ fontSize: 14, color: t.textMuted, fontFamily: font.body, marginBottom: 12 }}>
                No forms added yet. Click "Add Forms" to pick from the library.
              </div>
              <Button variant="primary" onClick={() => setShowLibrary(true)}>Browse Form Library</Button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {forms.map((f, i) => (
                <div key={f.id} style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
                  background: t.surface, border: `1px solid ${t.border}`, borderRadius: 10,
                  transition: "all 0.15s",
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 11, fontWeight: 700, fontFamily: font.mono,
                    background: f.is_complete ? t.emerald : t.pageBg,
                    color: f.is_complete ? "#fff" : t.textMuted,
                    border: f.is_complete ? "none" : `1.5px solid ${t.border}`,
                  }}>{f.is_complete ? "\u2713" : i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>{f.form_name}</div>
                    <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, textTransform: "capitalize" }}>{f.category}</div>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); handlePreview(f.id, f.form_name); }} style={{
                    background: "none", border: `1px solid ${t.border}`, borderRadius: 6, fontSize: 11, color: t.textSecondary,
                    cursor: "pointer", padding: "4px 10px", fontFamily: font.body, fontWeight: 500,
                  }} title="Preview form">Preview</button>
                  <button onClick={() => handleRemoveForm(f.id)} style={{
                    background: "none", border: "none", fontSize: 16, color: t.textMuted, cursor: "pointer", padding: "4px 8px",
                  }} title="Remove form">&times;</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        {forms.length > 0 && (
          <div style={{ display: "flex", gap: 12 }}>
            <Button variant="primary" size="lg" onClick={() => navigate(`/packets/${packetId}/fill`)}>
              Start Filling Forms
            </Button>
          </div>
        )}
      </div>

      {/* Form Library Modal */}
      {showLibrary && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.4)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }} onClick={() => setShowLibrary(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: t.surface, borderRadius: 16, width: isMobile ? "100%" : 600,
            maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          }}>
            {/* Modal header */}
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${t.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: t.navyLight, fontFamily: font.display }}>Form Library</div>
                <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
                  {selectedForms.length > 0 ? `${selectedForms.length} selected` : "Select forms to add to your packet"}
                </div>
              </div>
              <button onClick={() => setShowLibrary(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: t.textMuted }}>&times;</button>
            </div>

            {/* Search */}
            <div style={{ padding: "12px 20px 8px" }}>
              <Input placeholder="Search forms..." value={search} onChange={e => setSearch(e.target.value)} icon="\uD83D\uDD0D" />
            </div>

            {/* Form list by category */}
            <div style={{ flex: 1, overflowY: "auto", padding: "8px 20px 16px" }}>
              {Object.entries(filteredLibrary).map(([cat, catForms]) => (
                <div key={cat} style={{ marginBottom: 16 }}>
                  <div style={{
                    fontSize: 10, fontWeight: 700, color: t.gold, fontFamily: font.mono,
                    letterSpacing: "1px", textTransform: "uppercase", marginBottom: 8, padding: "4px 0",
                    borderBottom: `1px solid ${t.border}`,
                  }}>{CATEGORY_LABELS[cat] || cat}</div>
                  {catForms.map(form => {
                    const selected = selectedForms.includes(form.id);
                    return (
                      <div key={form.id} onClick={() => toggleFormSelection(form.id)} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                        borderRadius: 8, cursor: "pointer", marginBottom: 4,
                        background: selected ? `${t.emerald}10` : "transparent",
                        border: selected ? `1.5px solid ${t.emerald}40` : "1.5px solid transparent",
                        transition: "all 0.15s",
                      }}>
                        <div style={{
                          width: 20, height: 20, borderRadius: 4, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                          background: selected ? t.emerald : "transparent",
                          border: selected ? "none" : `1.5px solid ${t.border}`,
                          color: "#fff", fontSize: 11, fontWeight: 700,
                        }}>{selected ? "\u2713" : ""}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500, color: t.textPrimary, fontFamily: font.body }}>{form.name}</div>
                          {form.description && <div style={{ fontSize: 11, color: t.textMuted, fontFamily: font.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{form.description}</div>}
                        </div>
                        <span style={{ fontSize: 10, color: t.textMuted, fontFamily: font.mono, flexShrink: 0 }}>{form.fieldCount} fields</span>
                      </div>
                    );
                  })}
                </div>
              ))}
              {Object.keys(filteredLibrary).length === 0 && (
                <div style={{ textAlign: "center", padding: 40, color: t.textMuted, fontFamily: font.body }}>
                  {search ? "No forms match your search" : "All forms have been added to this packet"}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div style={{ padding: "12px 20px", borderTop: `1px solid ${t.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <Button variant="outline" onClick={() => { setShowLibrary(false); setSelectedForms([]); }}>Cancel</Button>
              <Button variant="primary" disabled={selectedForms.length === 0} onClick={handleAddForms}>
                Add {selectedForms.length} Form{selectedForms.length !== 1 ? "s" : ""}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewHtml && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.5)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }} onClick={() => setPreviewHtml(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 16, width: isMobile ? "100%" : 800,
            maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden",
            boxShadow: "0 24px 48px rgba(0,0,0,0.3)",
          }}>
            <div style={{
              padding: "14px 20px", borderBottom: `1px solid ${t.border}`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.navyLight, fontFamily: font.display }}>{previewName}</div>
              <button onClick={() => setPreviewHtml(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: t.textMuted }}>&times;</button>
            </div>
            <iframe
              srcDoc={previewHtml}
              title="Form Preview"
              style={{ flex: 1, border: "none", minHeight: isMobile ? 400 : 600, width: "100%" }}
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}
    </div>
  );
}
